import React, {
 useContext, useState, useEffect
} from 'react';
import {
    Card, CardHeader, CardTitle, CardContent
} from 'shared/components/ui/card';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Switch
} from 'shared/components/ui/switch';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Check, Copy, Save, BotOff
} from 'lucide-react';
import {
 applicationService
} from 'shared/api/services/BEXO';
import {
 OrganizationContext
} from 'shared/context/OrganizationContext';

import {
 Application
} from './types/builderApp.types';

interface AppInfoProps {
    application: Application | null;
    setApplication: React.Dispatch<React.SetStateAction<Application | null>>;
    applicationLoading: boolean;
}

function AppInfo({
    application,
    setApplication,
    applicationLoading
}: AppInfoProps) {
    const {
 addNewNotifcation
} = useContext(Context);
    const [switchLoading, setSwitchLoading] = useState<boolean>(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const {
 selectedOrganization
} = useContext(OrganizationContext);
    const [originalName, setOriginalName] = useState<string | null>(null);
    const [imageData, setImageData] = useState<string>(application?.settings?.app_logo || '');

    useEffect(() => {
        if (application && application.settings) {
            const timer = setTimeout(() => {
                setImageData(application.settings.app_logo || ''); // fallback to empty string if app_logo is missing
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [applicationLoading]);

    // Handle file upload and convert to base64 string
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageData(reader.result as string); // Store the base64 string
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmitLogo = async () => {
        if (imageData) {
            try {
                const updatedLogo = {
                    settings: {
                        default_page: '/login',
                        app_logo: imageData,
                    }
                };

                await applicationService.updateApplication(application.organization_slug, application.name, updatedLogo);
                addNewNotifcation('App logo uploaded', 'success');
            } catch (error) {
                console.error('Error to upload logo:', error);
                addNewNotifcation('Failed to upload logo', 'danger');
            }
        } else { /* empty */

        }
    };

    if (applicationLoading || !application) {
        return <p>Loading application details...</p>;
    }

    const baseUrl = process.env.REACT_APP_BEX3D_URL;
    let environmentPart = '';
    let domainPart = '';

    if (baseUrl) {
        const urlParts = baseUrl.split('.');

        if (baseUrl.includes('-')) {
            const [prefix, fullDomain] = baseUrl.split('-');
            if (prefix === 'dev') environmentPart = 'dev';
            else if (prefix === 'stg') environmentPart = 'stg';

            const domainParts = fullDomain.split('.');
            domainPart = domainParts.slice(1).join('.');
        } else {
            domainPart = urlParts.slice(1).join('.');
        }
    }

    const predefaultPageUrl = `https://${application.organization_slug}.${domainPart}/${application?.name?.toLowerCase().replace(/\s+/g, '-')}${application?.settings?.default_page || ''}`;
    const defaultPageUrl = environmentPart ? `${predefaultPageUrl}/${environmentPart}` : predefaultPageUrl;

    const fields = [
        {
 label: 'Organization ID',
value: application.tenant_id
},
        {
 label: 'Organization Name',
value: selectedOrganization.name
},
        {
 label: 'Application ID',
value: application.app_id
},
        {
 label: 'Application Full Name',
value: application.full_name
},
        {
 label: 'Organization Short Name',
value: application.organization_slug
},
        {
 label: 'Application Short Name',
value: application.name,
isEditable: true
},
        {
 label: 'Default Page',
value: defaultPageUrl,
isLink: true
}
    ];

    const handleToggleActive = async () => {
        setSwitchLoading(true);

        try {
            const newStatus = !application.active;
            await applicationService.toggleApplicationStatus(application.organization_slug, application.name, newStatus);

            setApplication((prevApp) => ({
 ...prevApp,
active: newStatus
}));
            addNewNotifcation(`Application status changed to ${newStatus ? 'active' : 'inactive'}`, 'success');
        } catch (error) {
            console.error('Error updating application status:', error);
            addNewNotifcation('Failed to update application status', 'danger');
        } finally {
            setSwitchLoading(false);
        }
    };

    const saveAppShortName = async (prevName: string) => {
        try {
            const updatedApp = {
 full_name: application.full_name,
name: application.name,
active: application.active
};
            await applicationService.updateApplication(application.organization_slug, prevName, updatedApp);
            addNewNotifcation('Application short name saved!', 'success');
        } catch (error) {
            console.error('Error saving short name:', error);
            addNewNotifcation('Failed to save short name', 'danger');
        }
    };

    const copyToClipboard = (value: string, label: string) => {
        navigator.clipboard.writeText(value);
        setCopiedField(label);
        setTimeout(() => setCopiedField(null), 2000);
    };

    return (
        <div className="flex flex-col overflow-y-scroll w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-0">
                <Card className="col-span-2">
                    <CardHeader>
                        <CardTitle>Application Info</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {fields.map((field) => (
                            <div key={field.label} className="flex items-center gap-2">
                                <Label htmlFor={field.label} className="w-1/3">{field.label}</Label>
                                <div className="w-full">
                                    {field.isLink ? (
                                        <a href={field.value} target="_blank" rel="noopener noreferrer" className="block w-full px-3 py-2 border rounded-md text-blue-600 dark:text-blue-400 underline text-sm bg-background" style={{
 height: '40px',
display: 'flex',
alignItems: 'center'
}}>
                                            {field.value}
                                        </a>
                                    ) : field.isEditable ? (
                                        <div className="flex items-center -ml-4">
                                            <Input id={field.label} value={field.value} onFocus={() => setOriginalName(field.value)} onChange={(e) => setApplication(prevApp => ({
 ...prevApp,
name: e.target.value
}))} className="w-full h-10" />
                                            <Button onClick={() => saveAppShortName(originalName as string)} variant="outline" className="ml-2" title="Save">
                                                <Save className="h-4 w-4 text-green-500" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <Input id={field.label} value={field.value} readOnly className="w-full h-10" />
                                    )}
                                </div>
                                {!field.isEditable && (
                                    <Button onClick={() => copyToClipboard(field.value, field.label)} variant="outline" className="ml-2">
                                        {copiedField === field.label ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                )}
                            </div>
                        ))}
                        <div className="flex items-center gap-4 mb-4">
                            <Label htmlFor="App Logo" className="w-1/3 text">Upload your App Logo</Label>
                            <div className="w-full flex flex-col">
                                <input type="file" accept="image/*" onChange={handleFileChange} className="border border-border rounded-lg p-2 mb-2 bg-background text-foreground" />
                                <Button onClick={handleSubmitLogo} className="w-full">
                                    Upload
                                </Button>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 mb-4">
                            <Label htmlFor="App Logo Preview" className="w-1/3 text">View App Logo</Label>
                            <div className="w-full flex gap-4 items-center">
                                <div className="flex-shrink-0">
                                    {imageData ? (
                                        <img src={imageData} alt="App Logo Preview" className="max-w-[400px] max-h-[100px] w-auto h-auto rounded-lg shadow-lg" />
                                    ) : (
                                        <p className="text-muted-foreground">No logo uploaded yet. Upload an image to see a preview.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className='w-full md:w-[350px] ml-auto'>
                    <CardHeader className="flex justify-between items-center">
                        <CardTitle>Application Status</CardTitle>
                        <Switch disabled={switchLoading} checked={application.active} onCheckedChange={handleToggleActive} />
                    </CardHeader>
                    <CardContent className="flex flex-col items-center">
                        {application.active ? (
                            <BotIcon className="w-48 h-56 text-primary" />
                        ) : (
                            <BotOff className="w-48 h-56 text-primary" />
                        )}
                        <p className="mt-2 text-lg font-semibold">
                            Application is {application.active ? 'Active' : 'Inactive'}
                        </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function BotIcon(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 8V4H8" />
            <rect width="16" height="12" x="4" y="8" rx="2" />
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            <path d="M15 13v2" />
            <path d="M9 13v2" />
        </svg>
    );
}

export default AppInfo;
